import React, {useCallback, useEffect, useRef} from 'react';
import { useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Marker} from "../types/types"

interface ClusterMarkersProps {
    markers: Marker[];
    sourceColors: Record<string, string>;
    onMarkerClick: (m: Marker) => void;
    CLUSTER_THRESHOLD?: number;
}

export const createLightBoltPin = (color: string): HTMLDivElement => {
    const pin = document.createElement('div');
    pin.style.cssText = `
        position: absolute;
        transform: translate(-50%, -100%);
        width: 24px;
        height: 24px;
        cursor: pointer;
    `;

    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg");
    svg.setAttribute("width", "100%");
    svg.setAttribute("height", "100%");
    svg.setAttribute("viewBox", "0 0 24 24");

    // Outer circle with white stroke
    const circle = document.createElementNS(svgNS, "circle");
    circle.setAttribute("cx", "12");
    circle.setAttribute("cy", "12");
    circle.setAttribute("r", "10");
    circle.setAttribute("fill", color);
    circle.setAttribute("stroke", "white");
    circle.setAttribute("stroke-width", "2");
    svg.appendChild(circle);

    // Slightly bigger lightning bolt
    const path = document.createElementNS(svgNS, "path");
    path.setAttribute("d", "M12 4 L8 12 H13 L11 20 L16 10 H12 L12 4 Z"); // bigger bolt
    path.setAttribute("fill", "white");
    svg.appendChild(path);

    pin.appendChild(svg);
    return pin;
};

const createTooltip = (text: string, color: string): HTMLDivElement => {
    const tooltip = document.createElement("div");
    tooltip.innerText = text;
    tooltip.style.cssText = `
        position: absolute;
        background: ${color};
        color: white;
        font-size: 14px;
        font-weight: 600;
        padding: 8px 14px;
        border-radius: 8px;
        border: 2px solid white;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        transform: translate(-50%, -130%);
        pointer-events: none;
        z-index: 1000;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
    `;
    return tooltip;
}

export const createSimplePin = (color: string): HTMLDivElement => {
    const pin = document.createElement('div');
    pin.style.cssText = `
        position: absolute;
        transform: translate(-50%, -100%) rotate(-45deg);
        width: 25px;
        height: 25px;
        background-color: ${color};
        border-radius: 50% 50% 50% 0;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        cursor: pointer;
    `;

    const inner = document.createElement('div');
    inner.style.cssText = `
        position: absolute;
        width: 40%;
        height: 40%;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        background-color: white;
        border-radius: 50%;
    `;
    pin.appendChild(inner);
    return pin;
};

const ClusterMarkers: React.FC<ClusterMarkersProps> = ({
                                                           markers,
                                                           sourceColors,
                                                           onMarkerClick,
                                                           CLUSTER_THRESHOLD = 20,
                                                       }: {
    markers: Marker[];
    sourceColors: Record<string, string>;
    onMarkerClick: (m: Marker) => void;
    CLUSTER_THRESHOLD?: number;
}) => {
    const map = useMap();
    const markerMapRef = useRef<Map<string, google.maps.marker.AdvancedMarkerElement[]>>(new Map());
    const clusterRef = useRef<MarkerClusterer | null>(null);
    const individualRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
    const activeModeRef = useRef<'cluster' | 'individual'>('individual');
    const latestMarkers = useRef<Marker[]>(markers);
    const latestBounds = useRef<google.maps.LatLngBounds | null>(null);

    const computeKey = (m: Marker) => `${m.source}-${m.lat}-${m.lng}`;
    const redrawVisibleMarkersOnly = (visible: Marker[]) => {
        if (!map) {
            console.warn('[ClusterMarkers] Map is not available yet.');
            return;
        }
        const newKeys = new Set(visible.map(computeKey));
        markerMapRef.current.forEach((arr, key) => {
            if (!newKeys.has(key)) {
                arr.forEach(am => {
                    am.map = null;
                    clusterRef.current?.removeMarker(am, false);
                });
                markerMapRef.current.delete(key);
            }
        });
        visible.forEach(m => {
            const key = computeKey(m);
            if (!markerMapRef.current.has(key)) {
                const pin = createLightBoltPin(sourceColors[m.source] || 'gray');
                pin.removeAttribute('title');
                const am = new (google.maps.marker as any).AdvancedMarkerElement({
                    map,
                    position: { lat: m.lat, lng: m.lng },
                    // title: m.name,
                    content: pin,
                });

                const tooltip = createTooltip(m.name ?? 'Unnamed', sourceColors[m.source] || '#444');
                let isHoovering = false;

                // pin.appendChild(tooltip);
                pin.addEventListener('mouseenter', () => {
                    isHoovering = true;
                    document.body.appendChild(tooltip);
                    updateTooltipPosition();
                    tooltip.style.opacity = '1';
                });
                pin.addEventListener('mouseleave', () => {
                    isHoovering = false;
                    tooltip.style.opacity = '0';
                    tooltip.remove();
                });

                const updateTooltipPosition = () => {
                    if (!isHoovering) return;
                    const rect = pin.getBoundingClientRect();
                    tooltip.style.position = 'fixed';
                    tooltip.style.left = `${rect.left + rect.width / 2}px`;
                    tooltip.style.top = `${rect.top}px`;
                    requestAnimationFrame(updateTooltipPosition);
                };
                am.addListener('click', () => {
                    tooltip.style.opacity = '0';
                    onMarkerClick(m);
                });
                markerMapRef.current.set(key, [am]);

                if (visible.length > CLUSTER_THRESHOLD) {
                    clusterRef.current ??= new MarkerClusterer({ map, markers: [] });
                    clusterRef.current.addMarker(am);
                }
            }
        });

        if (visible.length <= CLUSTER_THRESHOLD && clusterRef.current) {
            clusterRef.current.clearMarkers();
            clusterRef.current = null;
        }
    };

    useEffect(() => {
        latestMarkers.current = markers;
        if (!map || !latestBounds.current) return;

        redrawVisibleMarkersOnly(markers);
    }, [markers]);

    useEffect(() => {
        if (!map || !google.maps?.marker?.AdvancedMarkerElement) {
            console.warn('[ClusterMarkers] Map or AdvancedMarkerElement not ready.');
            return;
        }
        const drawMarkers = () => {
            const bounds = map.getBounds();
            if (!bounds) return;
            latestBounds.current = bounds;

            if (activeModeRef.current === 'cluster') {
                clusterRef.current?.clearMarkers();
                clusterRef.current = null;
            } else {
                individualRef.current.forEach((m) => m.map = null);
                individualRef.current = [];
            }

            const visible = latestMarkers.current.filter(m=> bounds.contains(new google.maps.LatLng(m.lat, m.lng)));
            redrawVisibleMarkersOnly(visible);
        };
        const debounce = (fn: () => void, delay: number) => {
            let timer: NodeJS.Timeout;
            return () => {
                clearTimeout(timer);
                timer = setTimeout(fn, delay);
            };
        };
        const debouncedDraw = debounce(() => {
            drawMarkers();
        }, 350);
        const boundsListener = map.addListener('bounds_changed', debouncedDraw);
        const zoomListener = map.addListener('zoom_changed', debouncedDraw);

        drawMarkers();

        return () => {
            google.maps.event.removeListener(boundsListener);
            google.maps.event.removeListener(zoomListener);
        };
    }, [map]);


        // activeModeRef.current = visible.length > CLUSTER_THRESHOLD ? 'cluster' : 'individual';
        // if (!map.getBounds()) {
        //     const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
        //        drawMarkers();
        //     });
        //     return () => {
        //         google.maps.event.removeListener(listener);
        //     };
        // }
        // drawMarkers();
        // if (visible.length > CLUSTER_THRESHOLD) {
        //     const clusterMarkers = visible.map(m => {
        //         const marker = new google.maps.marker.AdvancedMarkerElement({
        //             map,
        //             position: {lat: m.lat, lng: m.lng},
        //             title: m.name,
        //             content: createSimplePin(sourceColors[m.source] || 'gray'),
        //         });
        //         marker.addListener('click', () => onMarkerClick(m));
        //         return marker;
        //     });
        //     clusterRef.current = new MarkerClusterer({
        //        map,
        //        markers: clusterMarkers,
        //        renderer: {
        //            render: ({ count, position }) => {
        //                const div = document.createElement('div');
        //                div.style.cssText = `
        //                 background: #FF9800;
        //                 color: white;
        //                 border-radius: 50%;
        //                 padding: 8px;
        //                 min-width: 30px;
        //                 text-align: center;
        //                 font-size: 14px;
        //                `;
        //                div.textContent = String(count);
        //                return new google.maps.marker.AdvancedMarkerElement({
        //                    position,
        //                    content: div
        //                });
        //            }
        //        }
        //     });
        //     activeModeRef.current = 'cluster';
        // } else {
        //     individualRef.current = visible.map(m=> {
        //         const marker = new google.maps.marker.AdvancedMarkerElement({
        //             map,
        //             position: { lat: m.lat, lng: m.lng },
        //             title: m.name,
        //             content: createSimplePin(sourceColors[m.source] || 'gray'),
        //         });
        //         marker.addListener('click', () => onMarkerClick(m));
        //         return marker;
        //     });
        //     activeModeRef.current = 'individual';
        // }
    // useEffect(() => {
    //     drawMarkersRef.current = () => {
    //
    // }, [map, sourceColors, onMarkerClick, CLUSTER_THRESHOLD]);
    return null;
};

export default ClusterMarkers;
